import { memo, useState, useCallback, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { KstDataGrid } from '@/components/KstDataGrid';
import { Tooltip } from '@/components/Tooltip';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { getOfficerLabel } from '@/utils/auth';
import moment from 'moment';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

function getAuditLogGridColumns(): KstDataGridColumn[] {
  return [
    { field: 'displayId', name: 'ID', size: '50px' },
    {
      field: 'dateCreated',
      name: 'Date/Time',
      template: (e: any) =>
        e.dateCreated
          ? moment(e.dateCreated).format('MMM D, YYYY h:mm A')
          : '',
    },
    { field: 'loggedByFirstName', name: 'First' },
    { field: 'loggedByLastName', name: 'Last' },
    {
      field: 'kerktonRole',
      name: 'Role',
      template: (e: any) => e.kerktonRole || e.role || '',
    },
    { field: 'action', name: 'Action' },
    {
      field: 'entry',
      name: 'Entry',
      template: (e: any) => (e.entry ? String(e.entry).replace(/\n/g, '<br>') : ''),
    },
  ];
}

export interface AdvisoryAuditLogTabHandle {
  refresh: () => void;
}

interface AdvisoryAuditLogTabProps {
  isVisible: boolean;
}

export const AdvisoryAuditLogTab = memo(forwardRef<AdvisoryAuditLogTabHandle, AdvisoryAuditLogTabProps>(function AdvisoryAuditLogTab({
  isVisible,
}: AdvisoryAuditLogTabProps, ref) {
  const [loading, setLoading] = useState(false);
  const [gridRows, setGridRows] = useState<KstDataGridRows>({ entries: null });
  const [gridPager, setGridPager] = useState<KstDataGridPager>({
    curPage: 1,
    numPages: 1,
    records: 0,
    isVisible: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [totalPages, setTotalPages] = useState(1);
  const [includeViews, setIncludeViews] = useState(false);
  const [includeAcknowledgments, setIncludeAcknowledgments] = useState(false);
  const [displayIdFilter, setDisplayIdFilter] = useState('');
  const [loggedByNameFilter, setLoggedByNameFilter] = useState('');
  const hasLoadedRef = useRef(false);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const includeViewsRef = useRef(includeViews);
  includeViewsRef.current = includeViews;
  const includeAcknowledgmentsRef = useRef(includeAcknowledgments);
  includeAcknowledgmentsRef.current = includeAcknowledgments;
  // Refs mirror the applied filter values so loadAuditLog (a stable useCallback)
  // always reads the current values. The *AppliedRef refs track what was last
  // sent to the server so a blur without an actual change does not re-query.
  const displayIdRef = useRef(displayIdFilter);
  displayIdRef.current = displayIdFilter;
  const loggedByNameRef = useRef(loggedByNameFilter);
  loggedByNameRef.current = loggedByNameFilter;
  const displayIdAppliedRef = useRef('');
  const loggedByNameAppliedRef = useRef('');

  const columns = useMemo(() => getAuditLogGridColumns(), []);
  const officerLabel = useMemo(() => getOfficerLabel(), []);

  const loadAuditLog = useCallback((pageOverride?: number, includeViewsOverride?: boolean, includeAcknowledgmentsOverride?: boolean) => {
    setLoading(true);
    const page =
      pageOverride !== undefined ? pageOverride : currentPageRef.current;
    const includeViewEvents =
      includeViewsOverride !== undefined ? includeViewsOverride : includeViewsRef.current;
    const includeAcknowledgmentEvents =
      includeAcknowledgmentsOverride !== undefined ? includeAcknowledgmentsOverride : includeAcknowledgmentsRef.current;

    const operation = {
      data: {
        page: page,
        take: pageSize,
        skip: (page - 1) * pageSize,
      },
    };

    api.advisoryAuditLogEntries(
      page,
      String(pageSize),
      JSON.stringify(operation),
      includeViewEvents,
      includeAcknowledgmentEvents,
      displayIdRef.current,
      loggedByNameRef.current,
      (result: any) => {
        const newTotalCount = result.records || 0;
        const newTotalPages = Math.ceil(newTotalCount / pageSize) || 1;
        const entries = result.entries || [];

        setTotalPages(newTotalPages);
        setGridRows({ entries });
        setGridPager({
          curPage: page,
          numPages: newTotalPages,
          records: newTotalCount,
          isVisible: newTotalPages > 1,
        });
        setLoading(false);
      },
      () => {
        App.showPopupNotification('Error getting advisory audit log.', 'error');
        setLoading(false);
      }
    );
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: () => loadAuditLog(),
  }), [loadAuditLog]);

  // Load on first visibility
  useEffect(() => {
    if (isVisible && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadAuditLog();
    }
  }, [isVisible, loadAuditLog]);

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      currentPageRef.current = page;
      loadAuditLog(page);
    },
    [totalPages, loadAuditLog]
  );

  const toggleIncludeViews = useCallback(() => {
    setIncludeViews((prev) => {
      const next = !prev;
      includeViewsRef.current = next;
      setCurrentPage(1);
      currentPageRef.current = 1;
      loadAuditLog(1, next);
      return next;
    });
  }, [loadAuditLog]);

  const toggleIncludeAcknowledgments = useCallback(() => {
    setIncludeAcknowledgments((prev) => {
      const next = !prev;
      includeAcknowledgmentsRef.current = next;
      setCurrentPage(1);
      currentPageRef.current = 1;
      loadAuditLog(1, undefined, next);
      return next;
    });
  }, [loadAuditLog]);

  // Advisory # accepts digits only; strip anything else so the backend never
  // gets a value it would reject with a 422.
  const onDisplayIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    setDisplayIdFilter(digits);
    displayIdRef.current = digits;
  }, []);

  const onLoggedByNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLoggedByNameFilter(value);
    loggedByNameRef.current = value;
  }, []);

  // Re-query (reset to page 1) only when the applied value actually changed,
  // so a blur without an edit does not trigger a redundant request.
  const applyDisplayId = useCallback(() => {
    const next = displayIdRef.current.trim();
    if (next === displayIdAppliedRef.current) return;
    displayIdAppliedRef.current = next;
    setCurrentPage(1);
    currentPageRef.current = 1;
    loadAuditLog(1);
  }, [loadAuditLog]);

  const applyLoggedByName = useCallback(() => {
    const next = loggedByNameRef.current.trim();
    if (next === loggedByNameAppliedRef.current) return;
    loggedByNameAppliedRef.current = next;
    setCurrentPage(1);
    currentPageRef.current = 1;
    loadAuditLog(1);
  }, [loadAuditLog]);

  const onDisplayIdKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyDisplayId();
    }
  }, [applyDisplayId]);

  const onLoggedByNameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyLoggedByName();
    }
  }, [applyLoggedByName]);

  return (
    <div style={{ display: isVisible ? undefined : 'none' }}>
      <div className="advisory-audit-toolbar">
        <div className="advisory-audit-filter">
          <label className="advisory-audit-filter__label" htmlFor="advisory-audit-filter-displayid">Advisory #</label>
          <input
            id="advisory-audit-filter-displayid"
            type="text"
            inputMode="numeric"
            className="advisory-audit-filter__input advisory-audit-filter__input--displayid"
            aria-label="Advisory #"
            value={displayIdFilter}
            onChange={onDisplayIdChange}
            onKeyDown={onDisplayIdKeyDown}
            onBlur={applyDisplayId}
          />
        </div>
        <div className="advisory-audit-filter">
          <label className="advisory-audit-filter__label" htmlFor="advisory-audit-filter-officer">{officerLabel}</label>
          <input
            id="advisory-audit-filter-officer"
            type="text"
            className="advisory-audit-filter__input"
            placeholder="First or last name"
            value={loggedByNameFilter}
            onChange={onLoggedByNameChange}
            onKeyDown={onLoggedByNameKeyDown}
            onBlur={applyLoggedByName}
          />
        </div>
        {/* Grouped so the two toggles wrap to the next line TOGETHER when the
            toolbar narrows, instead of one stranding beside the inputs. */}
        <div className="advisory-audit-toggles">
          <label className="kst-toggle">
            <input
              type="checkbox"
              className="kst-toggle__input"
              checked={includeViews}
              onChange={toggleIncludeViews}
            />
            <span className="kst-toggle__switch"></span>
            <span
              className={`kst-toggle__label ${includeViews ? 'kst-toggle__label--on' : 'kst-toggle__label--off'}`}
            >
              Include View Events
              <Tooltip content={`View events record each time an ${officerLabel.toLowerCase()} opened an advisory.`} position="top" />
            </span>
          </label>
          <label className="kst-toggle">
            <input
              type="checkbox"
              className="kst-toggle__input"
              checked={includeAcknowledgments}
              onChange={toggleIncludeAcknowledgments}
            />
            <span className="kst-toggle__switch"></span>
            <span
              className={`kst-toggle__label ${includeAcknowledgments ? 'kst-toggle__label--on' : 'kst-toggle__label--off'}`}
            >
              Include Acknowledgments
              <Tooltip content="Acknowledgment events record each board review and each urgent advisory acknowledgment." position="top" />
            </span>
          </label>
        </div>
      </div>
      <div className="kst-results-panel">
        <div className="kst-results-body">
          <KstDataGrid
            columns={columns}
            rows={gridRows}
            pager={gridPager}
            showcheckbox={false}
            showheader={false}
            mobiletitlefield="action"
            loading={loading}
            onGoToPage={goToPage}
          />
        </div>
      </div>
    </div>
  );
}));
